import { NumberMode } from '@coast/schemas';
import { API } from '@shared/types/api';

/**
 * This class is used to format numbers using static class methods.
 *
 * Usage:
 * - NumberFormat.currency(1234.50)                   → $1,234.50
 * - NumberFormat.percent(0.75)                       → 75%
 * - NumberFormat.float(1234.5)                       → 1,234.5
 * - NumberFormat.editableFloat(1234.56)              → 1234.56
 * - NumberFormat.editableWholeCurrency(1234.00)      → 1234
 * - NumberFormat.editableFractionalCurrency(1234.56) → 1234.56
 *
 * Some notes: currency, float, and percent formatters are typically meant for
 * display purposes. The editable formatters are meant for input purposes, to
 * omit symbols like dollar signs, percent signs, and commas.
 */

const CURRENCY_PRECISION = 2;
const FLOAT_PRECISION = 3;

export class NumberFormat {
  static currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: CURRENCY_PRECISION,
    maximumFractionDigits: CURRENCY_PRECISION,
  }).format;

  static percent = new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: FLOAT_PRECISION,
  }).format;

  static float = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: FLOAT_PRECISION,
  }).format;

  static editableWholeCurrency = new Intl.NumberFormat('en-US', {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format;

  static editableFractionalCurrency = new Intl.NumberFormat('en-US', {
    useGrouping: false,
    minimumFractionDigits: CURRENCY_PRECISION,
    maximumFractionDigits: CURRENCY_PRECISION,
  }).format;

  static editableFloat = new Intl.NumberFormat('en-US', {
    useGrouping: false,
    maximumFractionDigits: FLOAT_PRECISION,
  }).format;

  static convertNumberModeToNumberFormat(fieldType: NumberMode): API.NumberFormat {
    switch (fieldType) {
      case 'currency':
        return 'CURRENCY';
      case 'percent':
        return 'PERCENT';
      default:
        return 'FLOAT';
    }
  }
}
